import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  FiActivity,
  FiAlertTriangle,
  FiBox,
  FiClipboard,
  FiCloud,
  FiDroplet,
  FiHeart,
  FiLayers,
  FiLock,
  FiShield,
  FiTool,
  FiTruck,
  FiUsers,
  FiZap,
} from "react-icons/fi";
import Layout from "../../components/Layout";
import TrainingHero from "./TrainingHero";
import ContactSection4 from "../contact/ContactSection4";

const SITE_URL = "https://mbasolutions.co";

const safetyCourses = [
  {
    title: "Fire Safety",
    desc: "Fire prevention, evacuation readiness, and emergency response for safer workplaces.",
    icon: FiAlertTriangle,
  },
  {
    title: "First Aid",
    desc: "Essential first-response skills to support injured colleagues until professional help arrives.",
    icon: FiHeart,
  },
  {
    title: "Construction Safety",
    desc: "Site hazards, controls, and safe working practices for construction environments.",
    icon: FiTool,
  },
  {
    title: "Incident Investigation",
    desc: "Structured methods to investigate incidents, identify root causes, and prevent recurrence.",
    icon: FiClipboard,
  },
  {
    title: "Confined Space Safety",
    desc: "Safe entry, atmospheric awareness, and control measures for confined space work.",
    icon: FiBox,
  },
  {
    title: "Permit to Work",
    desc: "Practical PTW systems that control high-risk activities and improve accountability.",
    icon: FiClipboard,
  },
  {
    title: "Environmental Social Governance (ESG)",
    desc: "Foundational ESG awareness supporting responsible operations and stakeholder trust.",
    icon: FiCloud,
  },
  {
    title: "Basic Life Support (BLS) & Emergency First Aid",
    desc: "Life-saving techniques and emergency first aid for workplace readiness.",
    icon: FiActivity,
  },
  {
    title: "Scaffolding Safety",
    desc: "Safe scaffolding practices, inspection awareness, and fall-prevention principles.",
    icon: FiLayers,
  },
  {
    title: "H2S Safety",
    desc: "Hydrogen sulfide hazard awareness, detection, and emergency response controls.",
    icon: FiDroplet,
  },
  {
    title: "Risk Assessment",
    desc: "Identify hazards, evaluate risk, and select practical controls for day-to-day work.",
    icon: FiShield,
  },
  {
    title: "Forklift Safety",
    desc: "Safe operation awareness for forklifts and material-handling environments.",
    icon: FiTruck,
  },
  {
    title: "Leading Firemen",
    desc: "Leadership skills for fire response roles, coordination, and emergency readiness.",
    icon: FiUsers,
  },
  {
    title: "Electrical Safety",
    desc: "Electrical hazard recognition, safe isolation awareness, and injury prevention.",
    icon: FiZap,
  },
  {
    title: "Lockout/Tagout (LOTO) System",
    desc: "Energy isolation practices that protect workers during maintenance and servicing.",
    icon: FiLock,
  },
  {
    title: "Manual Handling",
    desc: "Safe lifting and movement techniques that reduce musculoskeletal injury risk.",
    icon: FiUsers,
  },
  {
    title: "Safety Training Techniques",
    desc: "Methods for delivering effective toolbox talks and safety coaching on site.",
    icon: FiTool,
  },
  {
    title: "Waste Management",
    desc: "Responsible waste handling, segregation, and environmental care practices.",
    icon: FiBox,
  },
  {
    title: "Onshore Workplace Safety",
    desc: "Core safety behaviors and controls for onshore industrial operations.",
    icon: FiShield,
  },
  {
    title: "ISO 14001 Environmental Management Awareness",
    desc: "Introduction to EMS principles that support environmental compliance and improvement.",
    icon: FiCloud,
  },
  {
    title: "Chemical Safety & Handling",
    desc: "Safe storage, labeling, PPE, and handling practices for hazardous substances.",
    icon: FiDroplet,
  },
  {
    title: "General Workplace Safety",
    desc: "Foundational workplace safety awareness for employees across roles and sectors.",
    icon: FiShield,
  },
  {
    title: "Work at Height Safety",
    desc: "Fall prevention, equipment awareness, and controls for work-at-height tasks.",
    icon: FiAlertTriangle,
  },
  {
    title: "Gas Testing in Confined Space",
    desc: "Atmospheric testing awareness supporting safer confined space entry decisions.",
    icon: FiActivity,
  },
];

const SafetyTraining = () => {
  return (
    <Layout
      title="Safety Training Courses | MBA Solutions"
      description="Explore MBA Solutions safety training courses including fire safety, first aid, confined space, permit to work, risk assessment, LOTO, work at height, and more for safer workplaces."
      keywords="safety training courses, fire safety training, first aid training, confined space safety, permit to work, risk assessment training, LOTO training, work at height, MBA Solutions safety"
      canonical={`${SITE_URL}/safety-training`}
    >
      <TrainingHero
        badge="Workplace Safety Programs"
        title="Safety Training Courses"
        subtitle="Practical safety training designed to reduce risk, strengthen compliance, and build confident teams across industrial and commercial workplaces."
        icon={FiShield}
        primaryCta={{ label: "Enquire Now", to: "/contact" }}
        secondaryCta={{ label: "All Training", to: "/training" }}
      />

      <section className="bg-white py-14 md:py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-center max-w-3xl mx-auto mb-12 md:mb-16"
          >
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Explore Our Safety Training Courses
            </h2>
            <div
              className="w-20 h-1 bg-[#F86624] mx-auto rounded-full mb-5"
              aria-hidden="true"
            />
            <p className="text-gray-600 text-base md:text-lg leading-relaxed">
              From fire and first aid to confined space, LOTO, and work at
              height — MBA Solutions delivers practical programs that help
              organizations build safer systems and stronger site competence.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5 md:gap-6">
            {safetyCourses.map((course, index) => {
              const Icon = course.icon;
              return (
                <motion.article
                  key={course.title}
                  initial={{ opacity: 0, y: 18 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.35, delay: (index % 8) * 0.03 }}
                  whileHover={{ y: -6 }}
                  className="group bg-gray-50 hover:bg-white border border-gray-100 rounded-2xl p-5 md:p-6 shadow-sm hover:shadow-lg hover:border-[var(--bg-primary)]/20 transition-all h-full"
                >
                  <div className="w-11 h-11 rounded-xl bg-[var(--bg-primary)]/10 text-[var(--bg-primary)] flex items-center justify-center mb-4 group-hover:bg-[var(--bg-primary)] group-hover:text-white transition-colors">
                    <Icon size={20} aria-hidden="true" />
                  </div>
                  <h3 className="text-base font-bold text-gray-900 mb-2 leading-snug group-hover:text-[var(--bg-primary)] transition-colors">
                    {course.title}
                  </h3>
                  <p className="text-sm text-gray-600 leading-relaxed mb-4">
                    {course.desc}
                  </p>
                  <Link
                    to="/contact"
                    className="inline-flex text-sm font-semibold text-[var(--bg-primary)] hover:text-[#F86624] transition-colors"
                  >
                    Enquire →
                  </Link>
                </motion.article>
              );
            })}
          </div>
        </div>
      </section>

      <section
        className="bg-gray-50 pt-10 md:pt-14"
        aria-label="Contact for training courses"
      >
        <div className="max-w-3xl mx-auto px-4 text-center mb-2">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">
            Contact for Training Courses
          </h2>
          <div
            className="w-20 h-1 bg-[#F86624] mx-auto rounded-full mb-4"
            aria-hidden="true"
          />
          <p className="text-gray-600">
            Tell us which safety course you need — our team will help with
            schedules, delivery options, and group bookings.
          </p>
        </div>
        <ContactSection4 />
      </section>

      <section className="relative overflow-hidden py-14 md:py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-[#1a1a2e] via-[#3d1f5c] to-[var(--bg-primary)] text-white">
        <div className="relative max-w-3xl mx-auto text-center">
          <h2 className="text-2xl md:text-3xl font-bold mb-4">
            Build a Safer, More Capable Workforce
          </h2>
          <div className="w-20 h-1 bg-[#F86624] mx-auto rounded-full mb-5" aria-hidden="true" />
          <p className="text-white/85 mb-8 leading-relaxed">
            Partner with MBA Solutions for practical safety training that
            reduces risk, strengthens compliance, and protects your people.
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full bg-[#F86624] hover:bg-[#e55a1a] text-white font-semibold shadow-lg transition-all"
          >
            Request Safety Training
          </Link>
        </div>
      </section>
    </Layout>
  );
};

export default SafetyTraining;
